/**
 * The firm the console acts in through the Firm API. A firm's staff act in
 * their own firm (the server fixes it from the session); platform staff choose
 * one, sent as `X-Org-Id`, with its kind (`organizations.governance`, fixed at
 * creation) for the words the console speaks. The choice is a per-browser
 * convenience.
 */

import { createSignal } from "solid-js";
import type { OrgGovernance } from "@noctant/challenge-builder";

const STORAGE_KEY = "noctant.admin.firm.v2";

/** A firm a platform staff member chose: its id and its kind. */
export interface ChosenFirm {
  orgId: string;
  governance: OrgGovernance;
}

function stored(): ChosenFirm | null {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (typeof raw !== "object" || raw === null) return null;
    const { orgId, governance } = raw as Record<string, unknown>;
    return typeof orgId === "string" && (governance === "credits" || governance === "plan")
      ? { orgId, governance }
      : null;
  } catch {
    return null;
  }
}

const [firm, setFirm] = createSignal<ChosenFirm | null>(stored());

/** The chosen firm's id; none until one is chosen. */
export const firmId = (): string | null => firm()?.orgId ?? null;

/** The chosen firm's kind; none until one is chosen. */
export const firmGovernance = (): OrgGovernance | null => firm()?.governance ?? null;

export function chooseFirm(next: ChosenFirm | null): void {
  setFirm(next);
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the choice lasts this page.
  }
}
